import React, { useEffect, useState } from 'react';
import '../styles/LoginPage.css';  // Import styles
import Logo from './Logo.jsx';
import config from '../config.js';
import { clearStaleFlowStorage, LOGIN_ERROR, markFlowStarted, SIGNIN_INCOMPLETE_ERROR } from '../_helpers/oauthFlowState.js';
import { useT } from '../contexts/LocaleContext.jsx';

// Provider icons exported from the Figma login frame (30×30 SVGs).
import appleMusicIcon from '../assets/login/apple-music-icon.svg';
import googleIcon from '../assets/login/google-icon.svg';
import twitchIcon from '../assets/login/twitch-icon.svg';
import devIcon from '../assets/login/dev-icon.svg';

// Decorative "rhythm" dots under the login card, mirrored around the centre.
const RHYTHM_DOT_SIZES = ['xs', 'sm', 'md', 'lg', 'md', 'sm', 'xs'];

// Catalog message per recognised `?error=` code; anything else gets the
// generic message. The state holds the key so the text follows the locale.
// `signin-incomplete` comes from OAuthPage (ENG-406): a callback arrived with
// no flow state behind it, so nothing was exchanged. That is not an
// authentication failure to explain away — the round-trip simply did not
// finish — and the message says only that, so a visitor who would otherwise
// land on a silent login page knows to start again.
// The rest come from a failed exchange (ENG-515, LOGIN_ERROR); only the code
// is ever read from the URL, so free text there gets the generic message.
const ERROR_MESSAGE_KEYS = {
  [LOGIN_ERROR.UNAUTHORIZED]: 'login.errors.unauthorized',
  [SIGNIN_INCOMPLETE_ERROR]: 'login.errors.signinIncomplete',
  [LOGIN_ERROR.EXCHANGE_FAILED]: 'login.errors.exchangeFailed',
  [LOGIN_ERROR.ACCESS_DENIED]: 'login.errors.accessDenied',
  [LOGIN_ERROR.PROVIDER_ERROR]: 'login.errors.providerError',
  [LOGIN_ERROR.ACCOUNT_DISABLED]: 'login.errors.accountDisabled',
};

function LoginPage() {
  const t = useT();

  const { apiBaseUrl } = config;
  const [errorKey, setErrorKey] = useState(null);

  useEffect(() => {
    //if the url has a query parameter for error, display the error
    const q = new URLSearchParams(window.location.search);
    if (q.has('error')) {
      const message = q.get('error').toLowerCase();
      setErrorKey(Object.hasOwn(ERROR_MESSAGE_KEYS, message) ? ERROR_MESSAGE_KEYS[message] : 'login.errors.generic');
    }
  }, []);


  // All OAuth providers share the same login hand-off: remember which provider
  // was chosen (OAuthPage reads it back on callback) and send the browser to
  // the backend, which runs the PKCE exchange with the provider itself. This
  // page used to mint a verifier/challenge pair of its own and put the
  // challenge in this URL, but the backend never looked at either half, so the
  // pair only ever delayed the buttons behind a spinner (ENG-405). Account
  // linking now works the same way: the backend mints its state and pair too,
  // behind a single-use link ticket (ENG-472, ProfilePage.handleLinkAccount).
  //
  // Starting a login begins a brand-new round-trip, so drop whatever an earlier
  // login, account-linking attempt or older build left in localStorage first.
  // A returning user otherwise carries a stale `redirect` / `linking-provider`
  // into OAuthPage, which diverts the callback away from the login exchange
  // (HTTP 400) on every attempt until browser storage is cleared (ENG-319).
  // Done on click rather than on mount so merely opening /login in a second
  // tab cannot wipe a login that is mid-flight in another one.
  //
  // The provider is stamped with the moment of the hand-off, because it is
  // also what OAuthPage checks before exchanging a callback. Nothing clears it
  // if the user gives up at the provider's consent screen, so an unstamped
  // marker would sit in localStorage indefinitely for a crafted callback to
  // claim as its own (FLOW_MAX_AGE_MS).
  const handleLogin = (provider) => {
    clearStaleFlowStorage();
    localStorage.setItem('oauth-provider', provider);
    markFlowStarted();
    window.location.href = `${apiBaseUrl}/auth/${provider}/login`;
  };

  const handleGoogleLogin = () => handleLogin('google');
  const handleTwitchLogin = () => handleLogin('twitch');
  // NOTE: the ENG-318 Figma frame (3340:2665, marked AWAITING RED-LINE) titles
  // this button "Login with Apple Music" with the Apple Music badge, but the
  // backend flow it triggers is still the Sign in with Apple (Apple ID) PKCE
  // login from ENG-311. Apple's HIG requires the Apple logo plus
  // "Sign in / Continue with Apple" for that flow. The label follows the design
  // as the source of truth; the mismatch has been raised on ENG-318 and the
  // button must be re-titled or re-wired to an Apple Music provider before
  // launch.
  const handleAppleLogin = () => handleLogin('apple');

  const handleDevLogin = () => handleLogin('dev');

  return (
    <div className="login-page">
      <div className="login-page-hero">
        <Logo />

        <p className="login-page-tagline">{t('login.tagline')}</p>

        {errorKey && (
          <div className="login-page-error" role="alert">{t(errorKey)}</div>
        )}

        {/* Nothing has to be prepared before a provider can be chosen, so the
            buttons render straight away; the spinner here waited on the PKCE
            pair removed in ENG-405. */}
        <div className="login-page-login-buttons">
          <button className="login-page-login-button login-page-apple-login-button"
            onClick={handleAppleLogin}>
            <img className="login-page-login-icon" src={appleMusicIcon} alt="" aria-hidden="true" />
            <span className="login-page-login-label">{t('login.providers.appleMusic')}</span>
          </button>

          <button className="login-page-login-button login-page-google-login-button"
            onClick={handleGoogleLogin}>
            <img className="login-page-login-icon" src={googleIcon} alt="" aria-hidden="true" />
            <span className="login-page-login-label">{t('login.providers.google')}</span>
          </button>

          <button className="login-page-login-button login-page-twitch-login-button"
            onClick={handleTwitchLogin}>
            <img className="login-page-login-icon" src={twitchIcon} alt="" aria-hidden="true" />
            <span className="login-page-login-label">{t('login.providers.twitch')}</span>
          </button>

          {import.meta.env.DEV && (
            <button className="login-page-login-button login-page-dev-login-button"
              data-testid="dev-login-button"
              onClick={handleDevLogin}>
              {/* The design's 1.75px dashed (5,5) outline: CSS `border: dashed`
                  snaps to whole pixels and can't set dash lengths, so it's
                  drawn as an SVG stroke in the button's currentColor. */}
              <svg className="login-page-dev-login-border" aria-hidden="true" focusable="false">
                <rect x="0" y="0" width="100%" height="100%" rx="17.125" />
              </svg>
              <img className="login-page-login-icon" src={devIcon} alt="" aria-hidden="true" />
              <span className="login-page-login-label">{t('login.providers.dev')}</span>
            </button>
          )}
        </div>

        <div className="login-page-rhythm-dots" aria-hidden="true">
          {RHYTHM_DOT_SIZES.map((size, index) => (
            <span key={index} className={`login-page-rhythm-dot login-page-rhythm-dot--${size}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default LoginPage;